import {
  afterNextRender,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import type { VivestSelectOption } from '@vivest-dev/design-system-web-components';
import { Steps } from '../../../shared/components/steps/steps';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { Router } from '@angular/router';
import { FaixaParcelas, Regime } from '../../../core/models/modalidade-ir';
import { EnumTipoRegime } from '../../../core/enum/EnumTipoRegime';
import { ToastService } from '../../../shared/services/toast-service';
import { formatarMoeda, formatarValor } from '../../../shared/utils/formatadores';

/** Regime exibido no radio card, com o nome correspondente ao idTipoRegime. */
interface RegimeOpcao extends Regime {
  titulo: string;
}

const NOMES_REGIME: Record<EnumTipoRegime, string> = {
  [EnumTipoRegime.Progressivo]: 'Progressivo',
  [EnumTipoRegime.Regressivo]: 'Regressivo',
};

const formatoPercentual = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface AliquotaRegressiva {
  prazo: string;
  percentual: string;
}

/** Espaço que o dialog do vivest-modal deixa em volta do painel (16px em cima e embaixo). */
const RESPIRO_DO_MODAL = 32;

/** Elemento do vivest-modal (Lit): `updateComplete` resolve depois que o componente renderiza. */
type VivestModalElement = HTMLElement & { updateComplete?: Promise<unknown> };

const ALIQUOTAS_REGRESSIVAS: readonly AliquotaRegressiva[] = [
  { prazo: 'Acima de 10 anos', percentual: '20%' },
  { prazo: 'De 8 a 10 anos', percentual: '20%' },
  { prazo: 'De 6 a 8 anos', percentual: '20%' },
  { prazo: 'de 4 a 6 anos', percentual: '25%' },
  { prazo: 'de 2 a 4 anos', percentual: '30%' },
  { prazo: 'Até 2 anos', percentual: '35%' },
];

@Component({
  imports: [Steps],
  selector: 'app-modalidade-ir',
  styleUrl: './modalidade-ir.scss',
  templateUrl: './modalidade-ir.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ModalidadeIr implements OnInit {
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly injector = inject(Injector);
  private readonly modalComparativo = viewChild<ElementRef<VivestModalElement>>('modalComparativo');

  constructor() {
    // Se sair da tela com o modal aberto, a altura reservada não fica presa no body.
    inject(DestroyRef).onDestroy(() => this.liberarAlturaDoModal());
  }

  readonly stepAtual = signal(4);
  readonly totalSteps = signal(7);
  /** O modal começa fechado e é aberto pelo botão "Veja o comparativo completo". */
  readonly openModal = signal(false);
  readonly regimeSelecionado = signal<EnumTipoRegime | null>(null);
  readonly parcelaSelecionada = signal<number | null>(null);

  protected readonly regimes = signal<RegimeOpcao[]>([]);
  protected readonly aliquotasRegressivas = ALIQUOTAS_REGRESSIVAS;
  /** Faixa de parcelas retornada pela API; vazia (min > max) enquanto não responde. */
  private readonly faixaParcelas = signal<FaixaParcelas>({ min: 1, max: 0 });
  /** Pagamento único: o resgate sai de uma vez, sem escolha de parcelas. */
  private readonly pagamentoUnico = signal(false);

  /** Opções do mínimo ao máximo de parcelas (ex.: min 1 e max 12 → 1 a 12). */
  protected readonly parcelaOptions = computed<VivestSelectOption[]>(() => {
    const { min, max } = this.faixaParcelas();
    return Array.from({ length: Math.max(max - min + 1, 0) }, (_, i) => {
      const numero = String(min + i);
      return { value: numero, label: numero };
    });
  });

  private readonly regimeEscolhido = computed(() =>
    this.regimes().find((r) => r.idTipoRegime === this.regimeSelecionado()),
  );

  /** Com pagamento único ou valor líquido zerado não há o que parcelar: a escolha de parcelas fica oculta. */
  protected readonly exibirParcelas = computed(() => {
    const regime = this.regimeEscolhido();
    return !this.pagamentoUnico() && !!regime && regime.valorLiquido !== 0;
  });

  /** Valor líquido do regime selecionado dividido pela quantidade de parcelas escolhida. */
  readonly valorDividido = computed(() => {
    const parcelas = this.parcelaSelecionada();
    const regime = this.regimeEscolhido();
    return regime && parcelas ? regime.valorLiquido / parcelas : 0;
  });

  /**
   * O botão de continuar é habilitado com o regime escolhido e, quando há parcelas a escolher,
   * também com a parcela. Com pagamento único ou valor líquido zerado, basta o regime.
   */
  protected readonly podeContinuar = computed(
    () => this.regimeSelecionado() !== null && (!this.exibirParcelas() || this.parcelaSelecionada() !== null),
  );

  ngOnInit(): void {
    void this.carregarDados();
  }

  /**
   * Busca a simulação de cada regime, a faixa de parcelas disponíveis e a escolha já feita
   * anteriormente (regime e parcelas), que vem preenchida na tela; null deixa sem seleção.
   */
  protected async carregarDados(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getModalidadeIr();

      // Ignora regimes desconhecidos e mantém a ordem Progressivo → Regressivo.
      this.regimes.set(
        (body?.regimes ?? [])
          .filter((regime) => regime.idTipoRegime in NOMES_REGIME)
          .sort((a, b) => a.idTipoRegime - b.idTipoRegime)
          .map((regime) => ({ ...regime, titulo: NOMES_REGIME[regime.idTipoRegime] })),
      );

      const faixa = body?.parcelas ?? { min: 1, max: 0 };
      this.faixaParcelas.set(faixa);
      this.pagamentoUnico.set(body?.pagamentoUnico ?? false);

      // Só aproveita a escolha anterior se ela ainda for uma opção válida na tela. Com um único
      // regime não há o que escolher: ele já vem selecionado.
      const regimeAtual = body?.tipoRegimeAtual ?? null;
      const regimes = this.regimes();
      this.regimeSelecionado.set(
        regimes.length === 1
          ? regimes[0].idTipoRegime
          : regimes.some((regime) => regime.idTipoRegime === regimeAtual)
            ? regimeAtual
            : null,
      );

      const parcelaAtual = body?.qtdParcelaAtual ?? null;
      this.parcelaSelecionada.set(
        parcelaAtual !== null && parcelaAtual >= faixa.min && parcelaAtual <= faixa.max ? parcelaAtual : null,
      );
    } catch {
      this.toastService.show('Não foi possível carregar as informações. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** O vivest-select dispara `change` e expõe o valor escolhido na propriedade `value`. */
  protected onParcelaChange(event: Event): void {
    const valor = Number((event.target as HTMLElement & { value: string }).value);
    this.parcelaSelecionada.set(valor > 0 ? valor : null);
  }

  /** A API envia o percentual multiplicado por 100 (15 = 15%); o Intl espera a fração. */
  protected formatarPercentual(valor: number): string {
    return formatoPercentual.format(valor / 100);
  }

  protected readonly formatarMoeda = formatarMoeda;
  /** Só o número (ex.: 19.949,12), para quando o "R$" fica num span com tamanho menor. */
  protected readonly formatarValor = formatarValor;

  showModal() {
    this.openModal.set(true);
    afterNextRender(() => void this.reservarAlturaDoModal(), { injector: this.injector });
  }

  closeModal() {
    this.openModal.set(false);
    this.liberarAlturaDoModal();
  }

  /**
   * A página roda num iframe que o iframe-resizer ajusta à altura do conteúdo, e o modal (fixo)
   * não conta nessa altura. Quando a página é mais baixa que o modal (ex.: valor líquido zerado,
   * sem o bloco de parcelas), o iframe fica baixo e corta o modal. Por isso, enquanto ele está
   * aberto, o body ganha a altura do painel como mínimo e o iframe-resizer cresce o iframe.
   */
  private async reservarAlturaDoModal(): Promise<void> {
    const modal = this.modalComparativo()?.nativeElement;
    await modal?.updateComplete;

    const painel = modal?.shadowRoot?.querySelector<HTMLElement>('[part="modal"]');
    if (!painel || !this.openModal()) return;

    document.body.style.minHeight = `${painel.offsetHeight + RESPIRO_DO_MODAL}px`;
    window.parentIFrame?.resize();
  }

  private liberarAlturaDoModal(): void {
    if (!document.body.style.minHeight) return;

    document.body.style.minHeight = '';
    window.parentIFrame?.resize();
  }

  onReturn() {
    this.router.navigate(['/instituto-online/dados-cadastrais']);
  }

  /** Salva o regime e a quantidade de parcelas escolhidos e segue para o envio de documentos. */
  async onSubmit(): Promise<void> {
    const tipoRegime = this.regimeSelecionado();
    if (tipoRegime === null || !this.podeContinuar()) return;

    this.loaderService.showLoader('Salvando...');

    try {
      await this.httpClientService.saveModalidadeIr({
        tipoRegime,
        qtdParcelas: this.exibirParcelas() ? this.parcelaSelecionada() : 1,
      });
      this.router.navigate(['/instituto-online/upload-documentos']);
    } catch {
      this.toastService.show('Não foi possível salvar as informações. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

}
